import { Search, MapPin, Youtube, Facebook, Megaphone, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTypingAnimation } from '@/hooks/use-typing-animation';
import { useFadeInObserver } from '@/hooks/use-fade-in-observer';
import { useSiteContent } from '@/hooks/use-site-content';
import { useState } from 'react';

const ServiceCard = ({ service, index }: { service: any; index: number }) => {
  const { ref, isVisible } = useFadeInObserver(index * 100);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const Icon = service.icon;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const cardContent = (
    <Card 
      className={`border-border bg-card group h-full relative overflow-hidden transition-all duration-500 ${
        service.link ? 'cursor-pointer' : ''
      } ${isHovered ? 'border-primary/50 shadow-xl shadow-primary/10' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      style={{
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
      }}
    >
      {/* Dynamic gradient background */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(226, 78, 33, 0.08) 0%, transparent 50%)`,
        }}
      />
      
      {/* Animated border glow */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-[-1px] rounded-lg bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 blur-sm" />
      </div>

      <CardHeader className="relative z-10">
        <div 
          className={`w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-all duration-500 ${
            isHovered ? 'bg-primary/20 scale-110 rotate-3' : ''
          }`}
          aria-hidden="true"
        >
          <Icon 
            className={`w-7 h-7 text-primary transition-all duration-500 ${
              isHovered ? 'scale-110' : ''
            }`} 
            aria-hidden="true" 
          />
        </div>
        <CardTitle className={`text-xl transition-all duration-300 ${isHovered ? 'text-primary' : ''}`}>
          {service.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="relative z-10">
        <CardDescription className="text-base mb-4">{service.description}</CardDescription>
        <div className="flex items-center justify-between">
          <p className={`text-sm font-semibold transition-all duration-300 ${
            isHovered ? 'text-primary translate-x-1' : 'text-primary'
          }`}>
            {service.projects}
          </p>
          {service.link && (
            <span className={`text-xs font-medium text-primary/70 transition-all duration-300 ${
              isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
            }`}>
              Learn more →
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {service.link ? (
        <Link to={service.link} className="block h-full">
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
    </div>
  );
};

const ServicesSection = () => {
  const { getText } = useSiteContent('services');
  const sectionSubtitle = getText('section_subtitle', 'Services');
  const sectionHeading = getText('section_heading', 'Boost Business Strategic Solutions');
  const sectionDescription = getText('section_description', 'Business consulting consultants provide expert advice and guidance to businesses to help them improve their performance and efficiency.');

  const { ref: headingRef, displayedText } = useTypingAnimation(sectionHeading, 60);
  const { ref: sectionRef, isVisible: sectionVisible } = useFadeInObserver(0);
  
  const services = [
    {
      icon: Search,
      title: 'Search Engine Optimization',
      description: 'Improving search engine rankings and driving organic traffic through proven SEO strategies.',
      projects: '10+ Projects',
      link: null,
    },
    {
      icon: MapPin,
      title: 'Local SEO',
      description: 'Optimizing your business for local search results and Google Maps to attract nearby customers.',
      projects: '10+ Projects',
      link: '/services/local-seo',
    },
    {
      icon: Youtube,
      title: 'YouTube SEO',
      description: 'Maximizing video visibility and channel growth through strategic YouTube optimization.',
      projects: '30+ Projects',
      link: '/services/youtube-seo',
    },
    {
      icon: Facebook,
      title: 'Meta Ads',
      description: 'Creating high-converting Facebook and Instagram ad campaigns to reach your target audience.',
      projects: '20+ Projects',
      link: '/services/meta-ads',
    },
    {
      icon: Megaphone,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing solutions to boost your online presence and conversions.',
      projects: '50+ Projects',
      link: '/services/digital-marketing',
    },
    {
      icon: TrendingUp,
      title: 'E-commerce SEO',
      description: 'Boost your online store sales with product page optimization and e-commerce SEO strategies.',
      projects: '15+ Projects',
      link: '/services/ecommerce-seo',
    },
  ];

  return (
    <section id="services" className="py-24 bg-secondary/30 overflow-hidden">
      <div className="container mx-auto px-6">
        <div 
          ref={sectionRef}
          className={`text-center mb-16 transition-all duration-700 ${
            sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className={`text-primary font-semibold mb-2 uppercase tracking-wider text-sm transition-all duration-500 ${
            sectionVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`} style={{ transitionDelay: '100ms' }}>
            {sectionSubtitle}
          </p>
          <h2 ref={headingRef as any} className="text-4xl md:text-5xl font-bold mb-4 min-h-[3rem]">
            {displayedText}
          </h2>
          <p className={`text-muted-foreground text-lg max-w-2xl mx-auto transition-all duration-500 ${
            sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`} style={{ transitionDelay: '200ms' }}>
            {sectionDescription}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
